import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTarefaUpsert } from '@/api/mutations/tarefa';
import type { Tarefa, TipoTarefa, Energia, Prioridade } from '@/api/types';

const TIPOS: TipoTarefa[] = [
  'estudar_empresa',
  'escrever_audit',
  'enviar_d1',
  'follow_up_d4',
  'follow_up_d10',
  'ligar',
  'marcar_call',
  'enviar_proposta',
  'ouvir_call',
  'escrever_observacao',
  'atualizar_audit',
  'responder_lead',
];

interface TarefaFormProps {
  empresaId: string;
  tarefa?: Tarefa | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isoLocalDate(addHours = 24): string {
  const d = new Date(Date.now() + addHours * 60 * 60 * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TarefaForm({ empresaId, tarefa, open, onOpenChange }: TarefaFormProps) {
  const upsert = useTarefaUpsert();
  const [tipo, setTipo] = useState<TipoTarefa>('estudar_empresa');
  const [descricao, setDescricao] = useState('');
  const [dataVenc, setDataVenc] = useState(isoLocalDate(24));
  const [prioridade, setPrioridade] = useState<Prioridade>('media');
  const [energia, setEnergia] = useState<Energia>('media');

  useEffect(() => {
    if (open) {
      if (tarefa) {
        setTipo(tarefa.tipo);
        setDescricao(tarefa.descricao_livre ?? '');
        setDataVenc(tarefa.data_vencimento.slice(0, 16));
        setPrioridade(tarefa.prioridade);
        setEnergia(tarefa.energia_requerida);
      } else {
        setTipo('estudar_empresa');
        setDescricao('');
        setDataVenc(isoLocalDate(24));
        setPrioridade('media');
        setEnergia('media');
      }
    }
  }, [open, tarefa]);

  async function submit() {
    try {
      await upsert.mutateAsync({
        empresa_id: empresaId,
        id: tarefa?.id,
        payload: {
          tipo,
          descricao_livre: descricao || undefined,
          data_vencimento: new Date(dataVenc).toISOString(),
          prioridade,
          energia_requerida: energia,
        },
      });
      toast.success(tarefa ? 'Tarefa atualizada' : 'Tarefa criada');
      onOpenChange(false);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{tarefa ? 'Editar tarefa' : 'Nova tarefa'}</h3>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Tipo</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoTarefa)}
              className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Descrição</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="O que precisa ser feito"
              rows={2}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Vencimento</label>
              <Input
                type="datetime-local"
                value={dataVenc}
                onChange={(e) => setDataVenc(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Prioridade</label>
              <select
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value as Prioridade)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                <option value="alta">alta</option>
                <option value="media">média</option>
                <option value="baixa">baixa</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Energia</label>
              <select
                value={energia}
                onChange={(e) => setEnergia(e.target.value as Energia)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                <option value="alta">alta</option>
                <option value="media">média</option>
                <option value="baixa">baixa</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {upsert.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {tarefa ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
